import React, {
	useState,
	useEffect,
	useMemo,
	useCallback,
	useRef,
} from "react";
import { Col, Row } from "react-bootstrap";
import { useMutation, useQuery } from "react-query";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";

import { ScheduleAViewSelect, SplitProfileViewSelect } from "components";
import { HighRiskContext } from "components/contexts";
import { ViewContainer } from "components/layout";
import { hasPermissions, permissionList } from "helpers";
import { getSplitProfileInitialValues } from "helpers/residuals/dropDownInitialValues";
import { finalService, merchantInfoService } from "services";
import { merchantInfoActions, uiActions } from "store";
import { ErrorResponse, SplitProfileItem } from "types";

import HighRiskForm from "./highRiskForm";

import { ReactComponent as AlertIcon } from "static/images/alert-round.svg";
import styles from "./residuals.module.scss";

type UnderwritingResidualsProps = {
	applicationId: string;
	agentId: string;
	processorId: number;
	isEditable: boolean;
	canViewResidualsDetails: boolean;
};

type FormData = {
	isHighRisk?: boolean;
	splitProfileForm?: {
		value: string;
		name?: string;
		isHighRisk?: boolean;
	};
	scheduleAForm?: {
		value: string;
		name?: string;
	};
};

export const UnderwritingResiduals = ({
	applicationId,
	agentId,
	processorId,
	isEditable,
	canViewResidualsDetails,
}: UnderwritingResidualsProps) => {
	const dispatch = useDispatch();

	const [splitProfilePrevValue, setSplitProfilePrevValue] =
		useState<SplitProfileItem>();

	const { data: application, isFetching: isApplicationLoading } = useQuery(
		["getApplicationValues"],
		() => finalService.getApplication(applicationId),
		{
			enabled: !!applicationId,
		},
	);

	const isHighRiskState = useSelector(
		(state: RootStateOrAny) => state.merchantInfo.merchantInfo.isHighRisk,
	);

	const [showSplitProfileWarning, setShowSplitProfileWarning] =
		useState<boolean>();

	const [isHighRisk, setIsHighRisk] = useState<boolean | undefined>(
		undefined,
	);

	const [splitProfileHandleSubmit, setSplitProfileHandleSubmit] =
		useState<Function>();

	const [highRiskSplitProfile, setHighRiskSplitProfile] = useState<any>();
	const [receivedSplitProfile, setReceivedSplitProfile] = useState<any>();

	const [scheduleAHandleSubmit, setScheduleAHandleSubmit] =
		useState<Function>();

	const splitProfileInitial = useMemo(
		() =>
			getSplitProfileInitialValues({
				applicationSplitProfileId: application?.splitProfileId,
				receivedSplitProfile,
				highRiskSplitProfile,
				isHighRisk,
			}),
		[
			application?.splitProfileId,
			highRiskSplitProfile,
			isHighRisk,
			receivedSplitProfile,
		],
	);

	const scheduleAInitial = useMemo(
		() => application?.scheduleAId ?? "",
		[application?.scheduleAId],
	);

	const isResidualsEditable = useMemo(
		() =>
			hasPermissions(
				permissionList.ChangeScheduleAOrResidualSplitProfileAllocatedToAnApplication,
			) && isEditable,
		[isEditable],
	);

	const isHighRiskForm = useMemo(
		() =>
			isHighRisk === undefined ? isHighRiskState === "true" : isHighRisk,
		[isHighRisk, isHighRiskState],
	);

	const { mutate: submitData } = useMutation(
		(data?: FormData) => {
			return merchantInfoService.saveResiduals(applicationId, {
				splitProfileId: data?.splitProfileForm?.value,
				scheduleAId: data?.scheduleAForm?.value,
			});
		},
		{
			onSuccess: () => {
				dispatch(
					uiActions.showSuccessNotification({
						title: "The Residuals were successfully saved ",
					}),
				);
			},
			onError: error => {
				dispatch(uiActions.showErrorNotification({ title: error }));
			},
		},
	);

	const { mutateAsync: submitHighRisk, isLoading: isHighRiskLoading } =
		useMutation((isHighRisk: boolean) =>
			merchantInfoService.saveHighRiskMarker(
				applicationId,
				isHighRisk ? "true" : "false",
			),
		);

	const handleSaveForm = useCallback(async () => {
		let formData: FormData | undefined;
		let error = false;

		await splitProfileHandleSubmit?.().then(
			result =>
				result(
					data =>
						(formData = {
							splitProfileForm: data?.splitProfileList,
						}),
				)(),
			reason => (error = reason),
		);

		await scheduleAHandleSubmit?.().then(
			result =>
				result(
					data =>
						(formData = {
							...formData,
							scheduleAForm: data?.scheduleAList,
						}),
				)(),
			reason => (error = reason),
		);

		if (error === false) {
			submitData(formData);
		} else {
			dispatch(uiActions.showErrorNotification({ title: error }));
		}
	}, [dispatch, scheduleAHandleSubmit, splitProfileHandleSubmit, submitData]);

	const handleSaveHighRisk = useCallback(
		isHighRisk => {
			submitHighRisk(isHighRisk).then(
				({ split_profile_id }: { split_profile_id: string }) => {
					setReceivedSplitProfile(
						split_profile_id === null ? "" : split_profile_id,
					);

					dispatch(
						merchantInfoActions.setMerchantHighRisk(isHighRisk),
					);

					if (isResidualsEditable && !isHighRisk) {
						handleSaveForm();
					} else {
						dispatch(
							uiActions.showSuccessNotification({
								title: "High Risk was successfully changed",
							}),
						);
					}
				},
				(error: ErrorResponse) =>
					dispatch(uiActions.showErrorNotification({ title: error })),
			);
		},
		[dispatch, handleSaveForm, isResidualsEditable, submitHighRisk],
	);

	const handleHighRiskChange = useCallback(
		(isHighRisk: boolean) => {
			setIsHighRisk(isHighRisk);
			if (!isHighRisk) {
				setShowSplitProfileWarning(false);
			}
			handleSaveHighRisk(isHighRisk);
		},
		[handleSaveHighRisk],
	);
	const handleSplitProfileChange = useCallback(
		(value, restorePrevValue) => {
			if (isHighRiskForm && !value?.isHighRisk && value?.value !== "") {
				dispatch(
					uiActions.showModal("CONFIRMATION", {
						title: `Use non high risk Split Profile for high risk application?`,
						restoreFocus: false,
						onConfirm() {
							handleSaveForm();
						},
						onCancel() {
							restorePrevValue();
						},
					}),
				);
			} else {
				handleSaveForm();
			}
		},
		[dispatch, handleSaveForm, isHighRiskForm],
	);

	const handleScheduleAChange = useCallback(
		(_value, _restorePrevValue) => {
			handleSaveForm();
		},
		[handleSaveForm],
	);

	useEffect(() => {
		setShowSplitProfileWarning(
			isHighRiskForm && highRiskSplitProfile === undefined,
		);
	}, [highRiskSplitProfile, isHighRiskForm]);

	const highRiskCallback = useRef<any>();

	return (
		<HighRiskContext.Provider value={{ highRiskCallback }}>
			<div className={styles.contentContainer}>
				<ViewContainer>
					<Row>
						<Col sm={5}>
							<HighRiskForm
								isLoading={!agentId || isHighRiskLoading}
								isEditable={isEditable}
								highRiskChangeHandler={handleHighRiskChange}
							/>
						</Col>
						<Col sm={7}>
							{showSplitProfileWarning && (
								<Row>
									<Col className={styles.warningContainer}>
										<div className={styles.warningIcon}>
											<AlertIcon />
										</div>
										There is no high risk residual split
										profile that matches the agent revenue
										share to apply to the deal. A message
										will be sent to Agent relations to
										request for the profile to be created.
									</Col>
								</Row>
							)}
						</Col>
					</Row>
				</ViewContainer>
				<SplitProfileViewSelect
					agentId={agentId}
					isEditable={isResidualsEditable}
					canViewResidualsDetails={canViewResidualsDetails}
					initialSplitProfile={splitProfileInitial}
					isHighRisk={isHighRiskForm}
					isLoadingData={
						!application?.id ||
						isApplicationLoading ||
						isHighRiskLoading
					}
					section="Underwriting"
					setHandleSubmit={setSplitProfileHandleSubmit}
					getHighRiskItem={setHighRiskSplitProfile}
					onChange={handleSplitProfileChange}
					setPrevValue={setSplitProfilePrevValue}
					prevValue={splitProfilePrevValue}
				/>
				<ScheduleAViewSelect
					agentId={agentId}
					processorId={processorId}
					isEditable={isResidualsEditable}
					canViewResidualsDetails={canViewResidualsDetails}
					initialScheduleA={scheduleAInitial}
					isLoadingData={!application?.id || isApplicationLoading}
					section="Underwriting"
					setHandleSubmit={setScheduleAHandleSubmit}
					onChange={handleScheduleAChange}
				/>
			</div>
		</HighRiskContext.Provider>
	);
};
